import * as React from 'react';
import { Helmet } from 'react-helmet';
import { Fabric, PrimaryButton } from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { useBetreuer } from 'api/betreuer';
import { useKundeList } from 'api/kunde';
import { SelectOption } from 'formComponents/Select/types';
import classnames from 'classnames';
import { viewStyles } from 'styles/viewStyles';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { Select } from 'formComponents/Select/Select';
import { ValueType } from 'react-select';
import { Auftrag, AuftragFilter, useAuftragList } from 'api/auftrag';
import { AuftragItem } from 'components/Auftrag/AuftragItem';
import useDebouncedState from 'hooks/useDebouncedState';
import { AuftragPosition, AuftragPositionFilter, useAuftragPositionList } from 'api/auftragPosition';
import { DebouncedSearchBox } from 'formComponents/DebouncedSearchBox';
import { AuftragEdit, AuftragEditData } from 'components/Auftrag/AuftragEdit';
import { List } from '@fluentui/react/lib/List';
import AutoSizer from 'react-virtualized-auto-sizer';
import moment from 'moment-timezone';
import { FormDatePicker } from 'formComponents/FormDatePicker';
import { lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import usePersistentState from 'hooks/usePersistentState';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { formStyles } from 'styles/formStyles';
import { useHistory, useParams } from 'react-router-dom';
import { RecordViewUrlParams } from 'layout/App/types';
import { createRecordUrl, decodeRecordNr } from 'utils/formatter';
import { useMediaQuery } from 'react-responsive';
import { AuftragPositionenHeader } from 'components/Auftrag/AuftragPositionenHeader';
import { LieferArt } from 'formComponents/lookups/LieferArt';
import { useTerminList } from 'api/termin';
import { AuftragStatusMulti } from 'formComponents/lookups/AuftragStatusMulti';

const defaultMaxRows = 250;

const styles = mergeStyleSets({
    searchBar: {
        display: 'grid',
        gridTemplateColumns: '200px 150px 150px 150px 180px 170px 170px 5fr 1fr max-content max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    searchBarSmall1: {
        display: 'grid',
        gridTemplateColumns: '200px 150px 150px 150px 180px 5fr 1fr max-content max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    searchBarSmall2: {
        display: 'grid',
        gridTemplateColumns: '170px 170px',
        gridColumnGap: 10,
        marginTop: 10,
    },
});

const alleBetreuerOption = { value: '__all__', label: 'Alle Betreuer' };

interface FilterValues {
    maxRows: number;
    kNr?: number;
    nr?: string;
    abrufZu?: string;
    status: string;
    statusList: number[];
    betreuer: SelectOption;
    datumVon?: string;
    datumBis?: string;
    lArt: string;
    searchText: string;
}

const defaultFilterValues: FilterValues = {
    maxRows: defaultMaxRows,
    kNr: undefined,
    nr: undefined,
    abrufZu: '',
    status: '__all__',
    statusList: [],
    betreuer: alleBetreuerOption,
    datumVon: undefined,
    datumBis: undefined,
    lArt: '__all__',
    searchText: '',
};

export const AuftragView = (): JSX.Element => {
    const auth = useSelector(authSelector);
    const history = useHistory();
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const betreuer = useBetreuer();
    const [viewMode, setViewMode] = usePersistentState<'short' | 'long'>('long', 'AuftragView.viewMode');

    const { urlKundeNr, urlRecordNr } = useParams<RecordViewUrlParams>();
    const kNr = urlKundeNr ? parseInt(urlKundeNr, 10) : undefined;

    const isSmallScreenEmbedded = useMediaQuery({ query: `(max-width: 2100px)` });
    const isSmallScreenNotEmbedded = useMediaQuery({ query: `(max-width: 1750px)` });
    const isSmallScreen = urlKundeNr ? isSmallScreenEmbedded : isSmallScreenNotEmbedded;

    const auftragEditData = React.useMemo<AuftragEditData | null>(() => {
        if (urlRecordNr) {
            return {
                auNr: decodeRecordNr(urlRecordNr),
                kNr,
            };
        }
        return null;
    }, [kNr, urlRecordNr]);

    const kundeList = useKundeList({ nr: kNr || -1 });

    const betreuerFilterOptions = React.useMemo<SelectOption[]>(() => {
        const options: SelectOption[] = [];

        if (auth.rolle === 'AD') {
            options.push({
                value: auth.nr?.toString() || '',
                label: auth.name || '?',
            });
        } else {
            options.push(alleBetreuerOption);
            betreuer
                .filter((b) => b.aktiv && b.name)
                .sort((a, b) => a.name.localeCompare(b.name))
                .forEach((b) => options.push({ value: b.nr.toString(), label: b.name }));
        }

        return options;
    }, [auth, betreuer]);

    const [filterValues, debouncedFilterValues, setFilterValues] = useDebouncedState<FilterValues>(() => {
        if (auth.rolle === 'AD') {
            return {
                ...defaultFilterValues,
                betreuer: {
                    value: auth.kz || '',
                    label: auth.name || '?',
                },
            };
        }
        return {
            ...defaultFilterValues,
            betreuer: alleBetreuerOption,
        };
    }, 1000);

    const filter = React.useMemo<AuftragFilter>(() => {
        return {
            kNr,
            betreuer:
                debouncedFilterValues.betreuer.value === '__all__'
                    ? undefined
                    : parseInt(debouncedFilterValues.betreuer.value, 10),
            // status: debouncedFilterValues.status === '__all__' ? undefined :
            // parseInt(debouncedFilterValues.status, 10),
            statusList: debouncedFilterValues.statusList,
            nrSearch: debouncedFilterValues.nr,
            abrufZuSearch: debouncedFilterValues.abrufZu,
            datumVon: debouncedFilterValues.datumVon,
            datumBis: debouncedFilterValues.datumBis,
            lArt: debouncedFilterValues.lArt === '__all__' ? undefined : debouncedFilterValues.lArt,
            searchText: debouncedFilterValues.searchText,
            withKunde: true,
            maxRows: defaultMaxRows,
        };
    }, [kNr, debouncedFilterValues]);

    const auftragList = useAuftragList(filter);

    const positionenFilter = React.useMemo<AuftragPositionFilter>(() => {
        if (auftragList.isLoading || auftragList.isLoading || auftragList.isError) {
            return {};
        }

        return {
            auNrList: auftragList.data?.map((auftrag) => auftrag.nr) || [],
        };
    }, [auftragList]);

    const auftragPositionList = useAuftragPositionList(positionenFilter, { enabled: false });

    React.useEffect(() => {
        if (positionenFilter.auNrList) {
            auftragPositionList.refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positionenFilter]);

    const auftragPositionenByANr = React.useMemo<{ [key: string]: AuftragPosition[] }>(() => {
        if (
            auftragPositionList.isLoading ||
            auftragPositionList.isError ||
            (auftragPositionList.data?.length || 0) === 0
        ) {
            return {};
        }

        const newAuftragPositionenByANr: { [key: string]: AuftragPosition[] } = {};

        auftragPositionList.data?.forEach((auftragPosition) => {
            if (!newAuftragPositionenByANr[auftragPosition.auNr]) {
                newAuftragPositionenByANr[auftragPosition.auNr] = [];
            }

            newAuftragPositionenByANr[auftragPosition.auNr].push(auftragPosition);
        });

        return newAuftragPositionenByANr;
    }, [auftragPositionList]);

    const kNrArtikelGruppeNrAndArtikelNrList = React.useMemo(() => {
        const list: string[] = [];

        (auftragList.data || []).forEach((angebot) => {
            const positionen = auftragPositionenByANr[angebot.nr];
            if (positionen) {
                positionen.forEach((p) => {
                    if (p.artikelGruppeNr && p.artikelNr) {
                        list.push(angebot.kNr + '|' + p.artikelGruppeNr + '|' + p.artikelNr);
                    }
                });
            }
        });

        return list;
    }, [auftragList.data, auftragPositionenByANr]);

    const wiedervorlagen = useTerminList({
        terminStatus: 0,
        terminArt: 'W',
        kNrArtikelGruppeNrAndArtikelNrList,
    });

    const onRenderCell = React.useCallback(
        (auftrag: Auftrag | undefined): JSX.Element => {
            if (!auftrag) {
                return <div />;
            }

            return (
                <AuftragItem
                    key={auftrag.nr}
                    auftrag={auftrag}
                    withKunde={!urlKundeNr}
                    viewMode={viewMode}
                    withHeader={false}
                    auftragPositionen={auftragPositionenByANr[auftrag.nr] || []}
                    wiedervorlagen={wiedervorlagen.data || []}
                    editAuftrag={() => history.push(createRecordUrl('auftraege', urlKundeNr, auftrag.nr))}
                    refetch={() => {
                        wiedervorlagen.refetch().then();
                        auftragPositionList.refetch().then(() => {
                            auftragList.refetch().then();
                        });
                    }}
                />
            );
        },
        [urlKundeNr, viewMode, auftragPositionenByANr, wiedervorlagen, history, auftragPositionList, auftragList],
    );

    const renderDatumVon = () => (
        <FormDatePicker
            placeholder="Datum (von)"
            value={filterValues.datumVon}
            onChange={(newValue) => {
                setFilterValues({
                    ...filterValues,
                    datumVon: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                });
            }}
        />
    );

    const renderDatumBis = () => (
        <FormDatePicker
            placeholder="Datum (bis)"
            value={filterValues.datumBis}
            onChange={(newValue) => {
                setFilterValues({
                    ...filterValues,
                    datumBis: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                });
            }}
        />
    );

    if (auth.rolle === 'AD' && kundeList.data?.length === 1) {
        if (kundeList.data[0].betreuer && kundeList.data[0].betreuer !== auth.nr) {
            return (
                <Fabric className={viewStyles.container}>
                    <div className={viewStyles.listContainer}>
                        <div className={viewStyles.noData}>Keine Daten.</div>
                    </div>
                </Fabric>
            );
        }
    }

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <Fabric
            className={classnames(viewStyles.container, {
                [viewStyles.twoSearchBarLines]: isSmallScreen,
                [viewStyles.embedded]: !!urlKundeNr,
            })}
        >
            <Helmet>
                {urlKundeNr ? 'Kunde ' + urlKundeNr + ' | ' : ''}
                <title>{auftragEditData?.auNr ? 'Auftrag ' + auftragEditData?.auNr : 'Aufträge'}</title>
            </Helmet>
            <div className={isSmallScreen ? styles.searchBarSmall1 : styles.searchBar}>
                <DebouncedSearchBox
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            searchText: newValue === undefined ? '' : newValue,
                        });
                    }}
                />
                <DebouncedSearchBox
                    placeholder="Auftrag-Nr."
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            nr: newValue === undefined ? '' : newValue,
                        });
                    }}
                />
                <DebouncedSearchBox
                    placeholder="Abruf zu"
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            abrufZu: newValue === undefined ? '' : newValue,
                        });
                    }}
                />
                <LieferArt
                    value={filterValues.lArt || ''}
                    addAll
                    menuPlacement="bottom"
                    renderLabel="placeholder"
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            lArt: newValue,
                        });
                    }}
                />
                <Select
                    name="betreuer"
                    value={filterValues.betreuer}
                    options={betreuerFilterOptions}
                    onChange={(newValue: ValueType<SelectOption, false>): void => {
                        setFilterValues({
                            ...filterValues,
                            betreuer: newValue || alleBetreuerOption,
                        });
                    }}
                />

                {isSmallScreen || renderDatumVon()}
                {isSmallScreen || renderDatumBis()}

                <AuftragStatusMulti
                    value={filterValues.statusList.map((v) => v.toString())}
                    renderLabel="placeholder"
                    onChange={(newValues) => {
                        setFilterValues({
                            ...filterValues,
                            statusList: newValues.map((v) => parseInt(v, 10)),
                        });
                    }}
                />

                <div />

                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Kurzansicht"
                        checked={viewMode === 'short'}
                        onChange={(event, checked) => {
                            setViewMode(checked ? 'short' : 'long');
                        }}
                    />
                </div>

                {auth.rolle !== 'AD' && (
                    <PrimaryButton
                        onClick={() => history.push(createRecordUrl('auftraege', urlKundeNr, 'neu'))}
                        text="Neuer Auftrag"
                    />
                )}
            </div>

            {isSmallScreen && (
                <div className={styles.searchBarSmall2}>
                    {renderDatumVon()}
                    {renderDatumBis()}
                </div>
            )}

            {auftragList.isLoading || auftragList.isFetching ? (
                <LoadingIndicator />
            ) : (
                <div className={viewStyles.listContainer}>
                    {auftragList?.data && auftragList?.data.length === 0 ? (
                        <div className={viewStyles.noData}>Keine Aufträge.</div>
                    ) : (
                        <>
                            {viewMode === 'short' && (
                                <div className={viewStyles.listContainer}>
                                    <table className={formStyles.stickyPositionenHeaderTable}>
                                        <AuftragPositionenHeader />
                                    </table>
                                </div>
                            )}
                            <AutoSizer>
                                {({ height, width }) => (
                                    <div
                                        style={{
                                            width,
                                            height: height - 25,
                                            overflowX: 'hidden',
                                            overflowY: 'scroll',
                                        }}
                                    >
                                        <List
                                            key={
                                                viewMode +
                                                (auftragPositionList.isSuccess ? 1 : 0) +
                                                (wiedervorlagen.isSuccess ? 1 : 0)
                                            }
                                            items={auftragList.data || []}
                                            onRenderCell={onRenderCell}
                                        />
                                    </div>
                                )}
                            </AutoSizer>
                        </>
                    )}
                </div>
            )}
            {auftragEditData !== null && (
                <AuftragEdit
                    defaultKNr={auftragEditData.kNr}
                    auNr={auftragEditData.auNr}
                    onClose={(refetch) => {
                        history.push(createRecordUrl('auftraege', urlKundeNr));
                        if (refetch) {
                            auftragPositionList.refetch().then(() => {
                                auftragList.refetch().then();
                            });
                        }
                    }}
                    onReopen={(newAuNr: string) => {
                        history.push(createRecordUrl('auftraege', urlKundeNr));
                        auftragPositionList.refetch().then(() => {
                            auftragList.refetch().then(() => {
                                history.push(createRecordUrl('auftraege', urlKundeNr, newAuNr));
                            });
                        });
                    }}
                />
            )}
        </Fabric>
    );
};
